<template>
  <div>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer />
      <div class="d-flex justify-end align-center">
        <v-text-field
          class="mr-3"
          v-model="search"
          append-icon="mdi-magnify"
          label="Busca paquetes por nombre"
          solo
          rounded
          hide-details
          flat
          dense/>
        <NewCombo
          @created="addCombo"/>
      </div>
    </div>

    <v-flex xs12>
      <v-skeleton-loader
        v-show="overlay"
        type="table" />
      <v-data-table
        v-show="!overlay"
        :loading-text="table.title"
        :server-items-length="table.total"
        :headers="table.headers"
        :items="comboList.models"
        :options="table.options"
        :footer-props="{
          'items-per-page-options': [10, 20, 30, 40, 50]
        }"
        sort-by="name"
        @update:options="updateTable"
        class="elevation-1 fixed-header">
         <template v-slot:top>
          <div class="px-3 filters">
             <v-row>
                <v-col cols="3">
                  <v-btn
                    text
                    color="primary"
                    rounded
                    small
                    :to="{ name: 'ArchivedCombos' }">
                    Ver paquetes archivados
                  </v-btn>
                </v-col>
             </v-row>
          </div>
         </template>
        <template v-slot:item.name="{ item }">
          <a href="#" @click.prevent="openPanel(item)">
            {{ item.name }}
          </a>
        </template>
        <template v-slot:item.price="{ item }">
          {{ item.price | toFixNumber(2) | numberWithComma | prefix('$') }}
        </template>
        <template v-slot:item.cost="{ item }">
          {{ item.cost | toFixNumber(2) | numberWithComma | prefix('$') }}
        </template>
        <template v-slot:item.weeklySale="{ item }">
          {{ item.weeklySale | numberWithComma }}
        </template>
        <template v-slot:item.status="{ item }">
          <div :class="`${statusColors[item.status]}--text`">
            {{ status[item.status] }}
          </div>
        </template>
        <template v-slot:item.section="{ item }">
          <span v-if="item.section">
            {{ item.section.name }}
          </span>
          <span v-else>
            -
          </span>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <span class="mr-3">
              <v-tooltip left>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    depressed
                    fab
                    x-small
                    :href="exportPDF(item)"
                    target="_blank"
                    color="#26A69A">
                      <v-icon small color="white">mdi-file-document</v-icon>
                  </v-btn>
                </template>
                <span>Exportar PDF</span>
              </v-tooltip>
            </span>
            <span class="mr-3">
              <v-tooltip left>
                <template
                  v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    fab
                    x-small
                    depressed
                    color="#78909C"
                    @click="openArchiveComboDialog(item)"
                  >
                    <v-icon
                      color="white"
                      icon
                      small
                    >mdi-archive</v-icon>
                  </v-btn>
                </template>
              <span>Archivar paquete</span>
              </v-tooltip>
            </span>
            <span>
              <AttachCombo
                v-if="item.status === 'in-progress'"
                :item="item"
                @updated="load()"/>
            </span>
            <span class="mr-3">
              <EditCombo
                :item="item"
                @updated="load()"/>
            </span>
            <span>
              <DeleteCombo
                :item="item"
                @deleted="removeCombo(item)"/>
            </span>
          </div>
        </template>
      </v-data-table>
    </v-flex>

    <v-dialog @keydown.esc="archiveComboDialog=false" v-model="archiveComboDialog" persistent max-width="500px">
      <v-card>
        <v-card-title>
          <span>¿Archivar el paquete {{ dataCombo.name }}?</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="onCloseArchiveComboDialog">{{ $t('messages.no') }}</v-btn>
          <v-btn color="black darken-1" text @click="archiveCombo">{{ $t('messages.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ComboPanel
      :combo="selectedCombo"
      :isOpen="isPanelOpen"
      @close="closePanel"
      @updated="updateComboList"/>
  </div>
</template>

<script>
import NewCombo from '@/components/modals/NewCombo'
import EditCombo from '@/components/modals/EditCombo'
import DeleteCombo from '@/components/modals/DeleteCombo'
import AttachCombo from '@/components/modals/AttachCombo'
import ComboPanel from '@/components/sidePanels/AddRecipeToCombo'
import Combo from '@/models/Combo'
import ComboList from '@/models/collections/ComboList'
import { debounce } from 'lodash'

export default {
  name: 'Combos',
  components: {
    NewCombo,
    EditCombo,
    DeleteCombo,
    AttachCombo,
    ComboPanel
  },
  data () {
    let comboList = new ComboList()
    let combo = new Combo({}, comboList)

    return {
      combo,
      comboList,
      archiveComboDialog: false,
      dataCombo: {},
      isPanelOpen: false,
      overlay: false,
      section: this.$i18n.t('combos.section'),
      table: {
        title: this.$i18n.t('combos.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('combos.headers.name'), value: 'name' },
          { text: this.$i18n.t('combos.headers.price'), value: 'price', align: 'right' },
          { text: this.$i18n.t('combos.headers.cost'), value: 'cost', align: 'right' },
          // { text: this.$i18n.t('combos.headers.weeklySale'), value: 'weeklySale', align: 'right' },
          { text: this.$i18n.t('combos.headers.status'), value: 'status' },
          { text: this.$i18n.t('combos.headers.sections'), value: 'section' },
          { text: this.$i18n.t('combos.headers.actions'), value: 'actions' }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 50,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: ['name'],
          sortDesc: []
        }
      },
      selectedCombo: {},
      search: '',
      status: {
        'in-progress': 'En elaboración',
        'in-review': 'En revisión',
        'attached': 'En menú',
        'unattached': 'No adjuntado',
        'archived': 'Archivado'
      },
      statusColors: {
        'in-progress': 'grey',
        'in-review': 'info',
        'attached': 'primary',
        'unattached': 'danger',
        'archived': 'bluegrey'
      }
    }
  },
  watch: {
    'search': debounce(async function (val) {
      const params = {
        skip: 0
      }
      try {
        params.name = `/${val}/i`
        await this.load(params)
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    }, 300)
  },
  methods: {
    openArchiveComboDialog (item) {
      this.dataCombo = item
      this.archiveComboDialog = true
    },
    onCloseArchiveComboDialog () {
      this.dataCombo = {}
      this.archiveComboDialog = false
    },
    async archiveCombo () {
      this.overlayDialog = true
      try {
        await this.$api.put(`/v1/combos/${this.dataCombo._id}/`, { status: 'archived' })
        this.onCloseArchiveComboDialog()
        await this.load()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlayDialog = false
    },
    exportPDF (item) {
      let url = process.env.VUE_APP_API_URL
      url += `/v1/combos/${item._id}/download-pdf`
      return url
    },
    updateTable: async function (data) {
      this.overlay = true
      const { sortBy, sortDesc } = data
      this.table.options = data
      await this.load({
        limit: data.itemsPerPage,
        skip: (data.itemsPerPage * (data.page - 1)),
        sort: `${sortDesc[0] ? '-' : ''}${sortBy[0]}`
      })
      this.overlay = false
    },
    async load (params = null) {
      params = params || {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        sort: 'name'
      }
      try {
        const { response } = await this.combo.fetch({ params })
        this.comboList.replace([response.data.data])
        this.table.total = response.data.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    addCombo (combo) {
      this.comboList.add(combo)
      this.table.total = this.comboList.models.length
    },
    removeCombo (combo) {
      this.comboList.remove(combo)
      this.table.total = this.comboList.models.length
    },
    openPanel (item) {
      this.selectedCombo = item
      this.isPanelOpen = true
    },
    closePanel () {
      this.isPanelOpen = false
    },
    updateComboList (combo) {
      this.comboList.remove({ id: combo.$.id })
      this.comboList.add(combo)
      this.comboList.sort('name')
    }
  }
}
</script>
